import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import { ChevronDown, Users2, FileInput, CheckCircle2 } from "lucide-react";
import { useStore } from "@/lib/store";

export default function ActivityLog() {
  const { state } = useStore();
  const [open, setOpen] = useState(null);
  const [filter, setFilter] = useState("all");
  const emps = state.employees;
  const list = filter === "all" ? state.activity : state.activity.filter((a) => a.employee === filter);

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold text-white sm:text-4xl">Activity</h1>
        <p className="mt-1 text-sm text-slate-400">Every automated action is traceable — who, what, source, result and approval</p>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <button onClick={() => setFilter("all")} className={`rounded-full px-3 py-1.5 text-xs ${filter === "all" ? "bg-[#00E5FF] text-[#030A16]" : "border border-white/10 text-slate-300"}`} data-testid="activity-filter-all">All</button>
        {emps.map((e) => (
          <button key={e.id} onClick={() => setFilter(e.id)} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs ${filter === e.id ? "bg-[#00E5FF] text-[#030A16]" : "border border-white/10 text-slate-300"}`} data-testid={`activity-filter-${e.id}`}>
            <img src={e.avatar} alt="" className="h-4 w-4 rounded-full object-cover" /> {e.name}
          </button>
        ))}
      </div>

      <div className="glass overflow-hidden rounded-2xl">
        {list.length === 0 && <div className="p-8 text-center text-sm text-slate-500" data-testid="activity-empty">No activity for this filter yet.</div>}
        {list.map((a) => {
          const emp = emps.find((e) => e.id === a.employee);
          const Icon = Icons[a.icon] || Icons.Activity;
          const isOpen = open === a.id;
          return (
            <div key={a.id} className="border-b border-white/5" data-testid={`activity-row-${a.id}`}>
              <button onClick={() => setOpen(isOpen ? null : a.id)} className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-white/5">
                <span className="w-12 shrink-0 font-mono text-xs text-slate-500">{a.time}</span>
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[#00E5FF]/10 text-[#00E5FF]"><Icon size={15} /></span>
                {emp && <img src={emp.avatar} alt={emp.name} className="h-8 w-8 shrink-0 rounded-lg object-cover" />}
                <div className="min-w-0 flex-1"><div className="truncate text-sm text-slate-200"><span className="text-white">{emp?.name || a.employee}</span> — {a.task}</div><div className="truncate text-[11px] text-slate-500">{a.result}</div></div>
                <ChevronDown size={16} className={`shrink-0 text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <div className="grid grid-cols-1 gap-2 px-4 pb-4 pl-[4.75rem] sm:grid-cols-2">
                      <Detail icon={FileInput} label="Source information used" value={a.source} />
                      <Detail icon={Icons.Zap} label="Action taken" value={a.action} />
                      <Detail icon={Users2} label="Associates consulted" value={a.consulted.length ? a.consulted.map((c) => emps.find((e) => e.id === c)?.name || c).join(", ") : "None"} />
                      <Detail icon={CheckCircle2} label="Approval" value={a.approval} />
                      <Detail icon={Icons.Flag} label="Result" value={a.result} />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-white/8 bg-[#030A16]/40 p-2.5">
      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase text-slate-500"><Icon size={12} /> {label}</div>
      <div className="mt-0.5 text-sm text-slate-200">{value}</div>
    </div>
  );
}
